import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/axiosInstance';

const css = `
  @keyframes fadeInUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { to { transform:rotate(360deg); } }
  .ej-row:hover td { background:#f0f9ff !important; }
  .ej-search:focus { border-color:#0B3D91 !important; box-shadow:0 0 0 3px rgba(11,61,145,.1) !important; outline:none; }
  .ej-select:focus { border-color:#0B3D91 !important; outline:none; }
  .ej-amount:focus { border-color:#0B3D91 !important; outline:none; }
  @media(max-width:640px){
    .ej-table-view { display:none !important; }
    .ej-card-view  { display:flex !important; }
    .ej-toolbar    { flex-direction:column !important; }
    .ej-toolbar > * { width:100% !important; }
  }
  @media(min-width:641px){
    .ej-table-view { display:block !important; }
    .ej-card-view  { display:none !important; }
  }
`;

function Toast({ msg, onClose }) {
  const ok = msg.type === 'ok';
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, left: 20, zIndex: 9999, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <div style={{
        pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 20px', borderRadius: 12, maxWidth: 480, width: '100%',
        background: ok ? '#F0FDF4' : '#FEF2F2',
        border: `1px solid ${ok ? '#86EFAC' : '#FCA5A5'}`,
        boxShadow: '0 8px 24px rgba(0,0,0,.1)',
      }}>
        <span style={{ fontSize: '1.2rem' }}>{ok ? '✅' : '❌'}</span>
        <div style={{ flex: 1, fontSize: '.88rem', color: ok ? '#166534' : '#991B1B', fontWeight: 600 }}>{msg.text}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#6B7280' }}>✕</button>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8,
  fontSize: '.9rem', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
};

const fmtMoney = (n) => (n || n === 0) ? Number(n).toLocaleString('rw-RW') : '';

export default function UmusanzuEjoheza() {
  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState(null);

  useEffect(() => { loadYears(); }, []);
  useEffect(() => { loadSummary(year); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (msg) { const t = setTimeout(() => setMsg(null), 4000); return () => clearTimeout(t); }
  }, [msg]);

  const loadYears = async () => {
    try {
      const res = await axios.get('/api/ejoheza/years');
      const list = Array.isArray(res.data) ? res.data : [];
      setYears(list.includes(currentYear) ? list : [currentYear, ...list]);
    } catch {
      setYears([currentYear]);
    }
  };

  const loadSummary = useCallback(async (y) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/ejoheza/summary?year=${y}`);
      const data = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setRows(data.map(r => ({ ...r, status: r.status || 'Oya' })));
    } catch (err) {
      setMsg({ type: 'err', text: err?.response?.data?.error || 'Ntibyashobotse gusoma amakuru.' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const setStatus = (contributionId, status) => {
    setRows(prev => prev.map(r => r.contribution_id === contributionId ? { ...r, status } : r));
  };

  const setAmount = (contributionId, amafaranga) => {
    setRows(prev => prev.map(r => r.contribution_id === contributionId ? { ...r, amafaranga } : r));
  };

  const markAll = (status) => {
    setRows(prev => prev.map(r => visibleIds.has(r.contribution_id) ? { ...r, status } : r));
  };

  const saveAll = async () => {
    if (!rows.length) return;
    setSaving(true);
    try {
      await axios.post('/api/ejoheza/save', {
        year,
        records: rows
          .filter(r => r.contribution_id != null)
          .map(r => ({ contribution_id: r.contribution_id, status: r.status, amafaranga: r.amafaranga || null })),
      });
      setMsg({ type: 'ok', text: 'Byabitswe neza!' });
      loadYears();
    } catch {
      setMsg({ type: 'err', text: 'Kubika byanze. Ongera ugerageze.' });
    } finally {
      setSaving(false);
    }
  };

  // One row per household (umukuru w'urugo), filtered by search on the head name
  // or the location fields.
  const households = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.umukuru_w_urugo || '').toLowerCase().includes(term) ||
      (r.umudugudu || '').toLowerCase().includes(term) ||
      (r.akagari || '').toLowerCase().includes(term) ||
      (r.izina_ry_isibo || '').toLowerCase().includes(term)
    );
  }, [rows, search]);

  const visibleIds = useMemo(() => new Set(households.map(h => h.contribution_id)), [households]);

  const totalHouseholds = households.length;
  const paidCount = households.filter(h => h.status === 'Yego').length;
  const unpaidCount = totalHouseholds - paidCount;
  const totalCollected = households.reduce((sum, h) => sum + (h.status === 'Yego' ? Number(h.amafaranga) || 0 : 0), 0);

  const isPastYear = year !== currentYear;

  const locationLine = (h) => [h.umudugudu, h.akagari, h.izina_ry_isibo].filter(Boolean).join(' · ');

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16, animation: 'fadeInUp .4s ease' }}>
      <style>{css}</style>
      {msg && <Toast msg={msg} onClose={() => setMsg(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#0B3D91', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>🐷</div>
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0B1F3B', margin: 0 }}>Umusanzu wa Ejo Heza</h2>
            <p style={{ color: '#6B7280', fontSize: '.82rem', margin: '2px 0 0' }}>Kurikirana umusanzu w'ingo buri mwaka</p>
          </div>
        </div>
      </div>

      {/* Year selector */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
        <label style={{ display: 'block', fontWeight: 700, fontSize: '.85rem', color: '#374151', marginBottom: 8 }}>
          📅 Umwaka:
        </label>
        <select
          className="ej-select"
          style={{ ...inputStyle, background: '#fff', cursor: 'pointer', maxWidth: 220 }}
          value={year}
          onChange={e => setYear(Number(e.target.value))}
        >
          {years.map(y => (
            <option key={y} value={y}>{y}{y === currentYear ? ' (Uyu mwaka)' : ''}</option>
          ))}
        </select>
        {isPastYear && (
          <div style={{ marginTop: 10, fontSize: '.8rem', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
            🕓 Uri kureba amateka y'umwaka wa {year}.
          </div>
        )}

        {!loading && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            {[
              { label: 'Ingo Zose', value: totalHouseholds, bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', icon: '🏠' },
              { label: 'Zishyuye', value: paidCount, bg: '#F0FDF4', color: '#166534', border: '#86EFAC', icon: '✅' },
              { label: 'Zitishyuye', value: unpaidCount, bg: '#FEF2F2', color: '#991B1B', border: '#FCA5A5', icon: '❌' },
              { label: 'Amafaranga', value: `${fmtMoney(totalCollected)} Frw`, bg: '#FDF4FF', color: '#86198F', border: '#F0ABFC', icon: '💰' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20, background: s.bg, border: `1px solid ${s.border}`, color: s.color, fontWeight: 700, fontSize: '.88rem' }}>
                {s.icon} {s.label}: {s.value}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toolbar */}
      {!loading && rows.length > 0 && (
        <div className="ej-toolbar" style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 12px' }}>
            <span style={{ color: '#9CA3AF' }}>🔍</span>
            <input
              className="ej-search"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '.9rem', fontFamily: 'inherit' }}
              placeholder="Shakisha umukuru w'urugo, umudugudu, akagari..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '.8rem' }}>✕</button>}
          </div>
          <button onClick={() => markAll('Yego')} style={{ padding: '10px 16px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', whiteSpace: 'nowrap' }}>
            ✅ Zose Zishyuye
          </button>
          <button onClick={() => markAll('Oya')} style={{ padding: '10px 16px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', whiteSpace: 'nowrap' }}>
            ❌ Zose Zitishyuye
          </button>
          <button onClick={saveAll} disabled={saving} style={{ padding: '10px 20px', background: saving ? '#93C5FD' : '#0B3D91', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.88rem', whiteSpace: 'nowrap' }}>
            {saving ? '⏳ Kubika...' : '💾 Bika Byose'}
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6B7280' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#0B3D91', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ fontSize: '.9rem' }}>Gutegereza amakuru...</p>
        </div>
      ) : households.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#F9FAFB', borderRadius: 12, border: '2px dashed #E5E7EB' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>{search ? '🔍' : '🏠'}</div>
          <p style={{ fontSize: '1rem', fontWeight: 700, color: '#374151' }}>
            {search ? `Nta bisubizo bya "${search}"` : 'Nta ngo ziboneka'}
          </p>
          {!search && <p style={{ fontSize: '.85rem', color: '#6B7280', marginTop: 6 }}>Andika ingo mu ipaji ya "Ingo" kugira ngo zigaragare hano.</p>}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
          {/* Desktop table */}
          <div className="ej-table-view">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr>
                    {['#', "Umukuru w'Urugo", 'Aho Batuye', 'Yishyuye?', 'Amafaranga'].map(hd => (
                      <th key={hd} style={{ padding: '10px 16px', textAlign: 'left', background: '#F9FAFB', color: '#374151', fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{hd}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {households.map((h, i) => (
                    <tr key={h.household_id} className="ej-row" style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ width: 24, height: 24, borderRadius: 6, background: '#EFF6FF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.72rem', color: '#1D4ED8' }}>{i + 1}</span>
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '.88rem', color: '#0B1F3B', fontWeight: 600 }}>🏠 {h.umukuru_w_urugo || 'Urugo'}</td>
                      <td style={{ padding: '10px 16px', fontSize: '.8rem', color: '#6B7280' }}>{locationLine(h) || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 3, width: 'fit-content', gap: 2 }}>
                          <button
                            onClick={() => setStatus(h.contribution_id, 'Yego')}
                            style={{ padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '.78rem', background: h.status === 'Yego' ? '#16A34A' : 'transparent', color: h.status === 'Yego' ? '#fff' : '#6B7280' }}
                          >✅ Yego</button>
                          <button
                            onClick={() => setStatus(h.contribution_id, 'Oya')}
                            style={{ padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '.78rem', background: h.status === 'Oya' ? '#DC2626' : 'transparent', color: h.status === 'Oya' ? '#fff' : '#6B7280' }}
                          >❌ Oya</button>
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          className="ej-amount"
                          type="number"
                          min="0"
                          placeholder="Frw"
                          value={h.amafaranga ?? ''}
                          onChange={e => setAmount(h.contribution_id, e.target.value === '' ? null : Number(e.target.value))}
                          style={{ width: 110, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: '.82rem', fontFamily: 'inherit' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="ej-card-view" style={{ display: 'none', flexDirection: 'column', gap: 8, padding: 12 }}>
            {households.map(h => (
              <div key={h.household_id} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, color: '#0B1F3B', fontSize: '.88rem' }}>🏠 {h.umukuru_w_urugo || 'Urugo'}</div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 20, fontWeight: 700, fontSize: '.74rem',
                    background: h.status === 'Yego' ? '#DCFCE7' : '#FEE2E2',
                    color: h.status === 'Yego' ? '#166534' : '#991B1B',
                  }}>
                    {h.status === 'Yego' ? '✅ Yishyuye' : '❌ Ntiyishyuye'}
                  </span>
                </div>
                {locationLine(h) && <div style={{ fontSize: '.76rem', color: '#9CA3AF', marginBottom: 8 }}>{locationLine(h)}</div>}
                <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 3, gap: 2, marginBottom: 8 }}>
                  <button onClick={() => setStatus(h.contribution_id, 'Yego')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '.8rem', background: h.status === 'Yego' ? '#16A34A' : 'transparent', color: h.status === 'Yego' ? '#fff' : '#6B7280' }}>✅ Yego</button>
                  <button onClick={() => setStatus(h.contribution_id, 'Oya')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '.8rem', background: h.status === 'Oya' ? '#DC2626' : 'transparent', color: h.status === 'Oya' ? '#fff' : '#6B7280' }}>❌ Oya</button>
                </div>
                <input
                  className="ej-amount"
                  type="number"
                  min="0"
                  placeholder="Amafaranga (Frw)"
                  value={h.amafaranga ?? ''}
                  onChange={e => setAmount(h.contribution_id, e.target.value === '' ? null : Number(e.target.value))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: '.82rem', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>

          {/* Footer count + bottom save button for long lists */}
          <div style={{ textAlign: 'center', padding: '12px 16px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ color: '#6B7280', fontSize: '.8rem' }}>
              Igiteranyo: <strong style={{ color: '#0B3D91' }}>{totalHouseholds}</strong> {totalHouseholds === 1 ? 'urugo' : 'ingo'}
            </span>
            <button onClick={saveAll} disabled={saving} style={{ padding: '10px 20px', background: saving ? '#93C5FD' : '#0B3D91', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.88rem', whiteSpace: 'nowrap' }}>
              {saving ? '⏳ Kubika...' : '💾 Bika Byose'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
